import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Edit2, Truck, Search, X, Package, ClipboardList } from "lucide-react";
import "@/product-ux.css";

const emptyForm = { name: "", company_name: "", vat_number: "", phone: "", email: "", website: "", address: "", cap: "", city: "" };

export default function Suppliers() {
  const { canWrite } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { editing: supplier|null, form }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/suppliers");
      setItems(r.data);
    } catch { toast.error("Errore caricamento fornitori"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setModal({ editing: null, form: { ...emptyForm } });
  const openEdit = (s) => setModal({ editing: s, form: {
    name: s.name || "", company_name: s.company_name || "",
    vat_number: s.vat_number || "", phone: s.phone || "",
    email: s.email || "", website: s.website || "",
    address: s.address || "", cap: s.cap || "", city: s.city || "",
  }});
  const setField = (k, v) => setModal(m => ({ ...m, form: { ...m.form, [k]: v } }));

  const save = async () => {
    if (!modal.form.company_name.trim()) { toast.error("La ragione sociale è obbligatoria"); return; }
    setSaving(true);
    try {
      if (modal.editing) {
        await api.put(`/suppliers/${modal.editing.id}`, modal.form);
        toast.success("Fornitore aggiornato");
      } else {
        await api.post("/suppliers", modal.form);
        toast.success("Fornitore creato");
      }
      setModal(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare il fornitore "${s.company_name}"? Verrà rimosso dai prodotti collegati.`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Fornitore eliminato"); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const openDetail = (s, tab = "prodotti") => nav(`/suppliers/${s.id}?tab=${tab}`);

  const filtered = items.filter(s => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return [s.name, s.company_name, s.vat_number, s.email, s.city].some(v => (v || "").toLowerCase().includes(t));
  });

  return (
    <div className="workflow-ux p-4 pt-16 md:p-8 lg:p-12" data-testid="suppliers-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Anagrafica</div>
          <h1 className="font-serif text-3xl md:text-4xl">Fornitori</h1>
          <div className="text-sm text-[#707070] mt-1">
            Gestione fornitori di materie prime e packaging (dati interni, non pubblici).
          </div>
        </div>
        {canWrite && (
          <button onClick={openNew} className="btn-primary flex items-center" data-testid="supplier-new-btn">
            <Plus className="w-3 h-3 mr-2" strokeWidth={2} />Nuovo Fornitore
          </button>
        )}
      </div>

      <div className="mb-6 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#707070]" strokeWidth={1.5} />
        <input value={q} onChange={e=>setQ(e.target.value)}
               placeholder="Cerca per nome, ragione sociale, P.IVA, email, città..."
               className="input-luxury text-sm w-full !pl-10"
               data-testid="suppliers-search" />
      </div>

      {loading ? (
        <div className="text-sm text-[#707070]">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E5E5E0] rounded-sm" data-testid="suppliers-empty">
          <Truck className="w-8 h-8 mx-auto text-[#C5B39B] mb-3" strokeWidth={1.5} />
          <div className="text-sm text-[#707070]">{q ? "Nessun fornitore corrisponde alla ricerca." : "Nessun fornitore creato. Inizia da 'Nuovo Fornitore'."}</div>
        </div>
      ) : (
        <div className="border border-[#E5E5E0] bg-white overflow-x-auto" data-testid="suppliers-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E5E0] text-left text-[10px] tracking-luxury text-[#707070] uppercase">
                <th className="px-4 py-3 font-normal">Ragione sociale</th>
                <th className="px-4 py-3 font-normal">Referente</th>
                <th className="px-4 py-3 font-normal">P.IVA</th>
                <th className="px-4 py-3 font-normal">Città</th>
                <th className="px-4 py-3 font-normal">Prodotti Associati</th>
                <th className="px-4 py-3 font-normal w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="border-b border-[#F0F0EA] hover:bg-[#FAFAF7] transition group"
                    data-testid={`supplier-row-${s.id}`}>
                  <td className="px-4 py-3 font-serif text-[#1A1A1A]">{s.company_name}</td>
                  <td className="px-4 py-3 text-[#707070]">{s.name || "—"}</td>
                  <td className="px-4 py-3 text-[#707070]">{s.vat_number || "—"}</td>
                  <td className="px-4 py-3 text-[#707070]">{[s.cap, s.city].filter(Boolean).join(" ") || "—"}</td>
                  <td className="px-4 py-3">
                    {s.product_count > 0 ? (
                      <button onClick={()=>openDetail(s, "prodotti")}
                              className="inline-flex items-center gap-1.5 text-[#1A1A1A] hover:text-[#C5B39B] underline decoration-dotted underline-offset-4"
                              title="Vedi prodotti"
                              data-testid={`supplier-products-${s.id}`}>
                        <Package className="w-3.5 h-3.5" strokeWidth={1.5}/>
                        {s.product_count} prodott{s.product_count === 1 ? "o" : "i"}
                      </button>
                    ) : (
                      <span className="text-[#B0B0AA] text-xs">Nessuno</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end items-center gap-3">
                      <button onClick={()=>openDetail(s, "ordini")}
                              className="btn-primary text-xs inline-flex items-center gap-1.5 !px-3 !py-1.5"
                              title="Gestione ordini"
                              data-testid={`supplier-orders-${s.id}`}>
                        <ClipboardList className="w-3.5 h-3.5"/> Ordini
                      </button>
                      {canWrite && (
                        <div className="flex gap-3 opacity-0 group-hover:opacity-100 transition">
                          <button onClick={()=>openEdit(s)} className="text-[#707070] hover:text-[#1A1A1A]" title="Modifica"
                                  data-testid={`supplier-edit-${s.id}`}>
                            <Edit2 className="w-3.5 h-3.5"/>
                          </button>
                          <button onClick={()=>remove(s)} className="text-[#8C3A3A] hover:opacity-70" title="Elimina"
                                  data-testid={`supplier-delete-${s.id}`}>
                            <Trash2 className="w-3.5 h-3.5"/>
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="workflow-ux fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="supplier-modal">
          <div className="bg-white w-full max-w-2xl border border-[#E5E5E0] shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0F0EA]">
              <h2 className="font-serif text-xl">{modal.editing ? "Modifica Fornitore" : "Nuovo Fornitore"}</h2>
              <button onClick={()=>setModal(null)} className="text-[#707070] hover:text-[#1A1A1A]" data-testid="supplier-modal-close">
                <X className="w-4 h-4"/>
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-tiny">Ragione sociale *</label>
                <input className="input-luxury" data-testid="supplier-input-company" placeholder="es. Laboratorio S.r.l." autoFocus
                       value={modal.form.company_name} onChange={e=>setField("company_name", e.target.value)} />
              </div>
              <div>
                <label className="label-tiny">Nome Referente</label>
                <input className="input-luxury" data-testid="supplier-input-name" placeholder="Nome e cognome"
                       value={modal.form.name} onChange={e=>setField("name", e.target.value)} />
              </div>
              <div>
                <label className="label-tiny">P.IVA</label>
                <input className="input-luxury" data-testid="supplier-input-vat" placeholder="es. IT12345678901"
                       value={modal.form.vat_number} onChange={e=>setField("vat_number", e.target.value)} />
              </div>
              <div>
                <label className="label-tiny">Telefono</label>
                <input className="input-luxury" data-testid="supplier-input-phone" placeholder="es. +39 02 1234567"
                       value={modal.form.phone} onChange={e=>setField("phone", e.target.value)} />
              </div>
              <div>
                <label className="label-tiny">Email</label>
                <input className="input-luxury" data-testid="supplier-input-email" type="email" placeholder="fornitore@esempio.it"
                       value={modal.form.email} onChange={e=>setField("email", e.target.value)} />
              </div>
              <div>
                <label className="label-tiny">Sito web</label>
                <input className="input-luxury" data-testid="supplier-input-website" placeholder="es. www.laboratorio.it"
                       value={modal.form.website} onChange={e=>setField("website", e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="label-tiny">Indirizzo</label>
                <input className="input-luxury" data-testid="supplier-input-address" placeholder="Via e numero civico"
                       value={modal.form.address} onChange={e=>setField("address", e.target.value)} />
              </div>
              <div>
                <label className="label-tiny">CAP</label>
                <input className="input-luxury" data-testid="supplier-input-cap" placeholder="es. 20100"
                       value={modal.form.cap} onChange={e=>setField("cap", e.target.value)} />
              </div>
              <div>
                <label className="label-tiny">Città</label>
                <input className="input-luxury" data-testid="supplier-input-city" placeholder="es. Milano"
                       value={modal.form.city} onChange={e=>setField("city", e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#F0F0EA]">
              <button onClick={()=>setModal(null)} className="btn-ghost text-sm" data-testid="supplier-cancel-btn">Annulla</button>
              <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="supplier-save-btn">
                {saving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
